import type { JSX } from 'react';
import { Button } from '@/app/ui/controls';
import type { ContentIssue } from '@/core/content/types';
import type { LayoutWarning } from '@/core/render/scene';
import styles from './IssueList.module.css';

/**
 * Сообщения о проблемах.
 *
 * Цвет — не единственный признак: у каждого уровня есть текстовая пометка,
 * поэтому сообщение остаётся понятным и без восприятия цвета.
 */

const LEVEL_LABEL: Record<string, string> = {
  error: 'Ошибка',
  warning: 'Предупреждение',
  info: 'Пояснение',
};

export type IssueListProps = {
  issues: (ContentIssue | LayoutWarning)[];
  onFix?: (value: string) => void;
  title?: string;
  /** Позволяет полю ввода сослаться на этот блок через aria-describedby. */
  id?: string;
};

export function IssueList({ issues, onFix, title, id }: IssueListProps): JSX.Element | null {
  if (issues.length === 0) return null;
  const hasBlocking = issues.some((i) => i.level === 'error');

  return (
    <div className={styles.wrap} id={id}>
      {title ? <h3 className={styles.title}>{title}</h3> : null}
      <ul className={styles.list} aria-live={hasBlocking ? 'assertive' : 'polite'}>
        {issues.map((issue, index) => (
          <li key={`${issue.code}-${index}`} className={styles[issue.level]}>
            <span className={styles.badge}>{LEVEL_LABEL[issue.level]}</span>
            <span className={styles.text}>
              {issue.message}
              {'fix' in issue && issue.fix && onFix ? (
                <span className={styles.fix}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      onFix(issue.fix!.value);
                    }}
                  >
                    {issue.fix.label}
                  </Button>
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
